import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import type { CommunicationInboxItem } from "@/types/communications";
import type { ReactNode } from "react";

interface CommunicationMessageRowProps {
  item: CommunicationInboxItem;
  selected: boolean;
  onSelect: (id: string) => void;
  onClick: (id: string) => void;
  isActive?: boolean;
  disabled?: boolean;
  trailingBadge?: ReactNode;
}

export function CommunicationMessageRow({
  item,
  selected,
  onSelect,
  onClick,
  isActive = false,
  disabled = false,
  trailingBadge,
}: CommunicationMessageRowProps) {
  const dateStr = (() => {
    try {
      return new Date(item.date).toLocaleString(undefined, {
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
      });
    } catch {
      return item.date;
    }
  })();

  return (
    <div
      className={cn(
        "flex items-start gap-3 rounded-md border px-3 py-2.5 cursor-pointer transition-colors hover:bg-muted/50",
        isActive && "border-primary/40 bg-primary/5",
        !item.read && "bg-muted/20"
      )}
      onClick={() => !disabled && onClick(item.id)}
    >
      <Checkbox
        checked={selected}
        disabled={disabled}
        onCheckedChange={() => onSelect(item.id)}
        onClick={(e) => e.stopPropagation()}
        className="mt-0.5"
      />
      <div className="min-w-0 flex-1">
        <div className="flex items-center justify-between gap-2">
          <p className={cn("text-sm truncate", !item.read && "font-semibold")}>{item.subject}</p>
          <div className="flex items-center gap-1.5 shrink-0">
            {trailingBadge}
            <span className="text-xs text-muted-foreground">{dateStr}</span>
          </div>
        </div>
        <p className="text-xs text-muted-foreground truncate mt-0.5">{item.from}</p>
        <p className="text-xs text-muted-foreground line-clamp-2 mt-1">{item.preview}</p>
        {item.type && (
          <Badge variant="outline" className="text-[10px] mt-1.5 capitalize">
            {item.type}
          </Badge>
        )}
      </div>
    </div>
  );
}
